import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

const SELECTORS = {
	root: '[data-uc-blog-grid]',
	chips: '[data-uc-blog-grid-chips]',
	chip: '[data-uc-blog-grid-chip]',
	content: '[data-uc-blog-grid-content]',
	pagination: '[data-uc-blog-grid-pagination]',
	sortForm: '[data-uc-blog-grid-sort]',
	sortSelect: '[data-uc-blog-grid-sort] select',
} as const

type CacheEntry = { html: string }

export class BlogGrid extends UcoastEl {
	static htmlSelector = 'blog-grid'
	static cache: Map<string, CacheEntry> = new Map()

	sectionId: string
	chipsEl: HTMLElement
	contentEl: HTMLElement
	popStateHandler: (event: PopStateEvent) => void

	constructor() {
		super()
		this.sectionId = q.ra(this, 'data-section-id')
		this.chipsEl = q.rs(SELECTORS.chips, this)
		this.contentEl = q.rs(SELECTORS.content, this)

		this.onChipClick = this.onChipClick.bind(this)
		this.onPaginationClick = this.onPaginationClick.bind(this)
		this.onSortChange = this.onSortChange.bind(this)
		this.popStateHandler = this.onPopState.bind(this)

		this.bindChips()
		this.bindPagination()
		this.bindSort()
	}

	override connectedCallback() {
		super.connectedCallback()
		window.addEventListener('popstate', this.popStateHandler)
		this.hydrateFromUrl()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		window.removeEventListener('popstate', this.popStateHandler)
	}

	hydrateFromUrl() {
		const params = new URLSearchParams(window.location.search)
		const category = params.get('category') ?? ''
		this.setAttribute('data-active-category', category)

		const chips = q.ol<HTMLAnchorElement>(SELECTORS.chip, this.chipsEl)
		chips?.forEach((chip) => {
			const handle = chip.getAttribute('data-category-handle') ?? ''
			const isActive = handle === category
			chip.classList.toggle('is-active', isActive)
			if (isActive) {
				chip.setAttribute('aria-current', 'page')
			} else {
				chip.removeAttribute('aria-current')
			}
		})

		this.filterCardsByCategory(category)
	}

	filterCardsByCategory(category: string) {
		const items = this.contentEl.querySelectorAll<HTMLElement>('.main-blog-grid__item')
		items.forEach((item) => {
			const card = item.querySelector<HTMLElement>('[data-category-handle]')
			if (!card) {
				item.hidden = false
				return
			}
			const handle = card.getAttribute('data-category-handle') ?? ''
			item.hidden = category !== '' && handle !== category
		})
	}

	bindChips() {
		const chips = q.ol<HTMLAnchorElement>(SELECTORS.chip, this.chipsEl)
		chips?.forEach((chip) => chip.addEventListener('click', this.onChipClick))
	}

	bindPagination() {
		const pagination = q.os(SELECTORS.pagination, this)
		if (!pagination) return
		pagination.addEventListener('click', this.onPaginationClick)
	}

	bindSort() {
		const select = q.os<HTMLSelectElement>(SELECTORS.sortSelect, this)
		if (!select) return
		select.addEventListener('change', this.onSortChange)
		// Stop the form from doing a native GET submit.
		const form = q.rs<HTMLFormElement>(SELECTORS.sortForm, this)
		form.addEventListener('submit', (e) => e.preventDefault())
	}

	onChipClick(event: MouseEvent) {
		const anchor = q.rct<MouseEvent, HTMLAnchorElement>(event)
		event.preventDefault()
		this.setActiveChip(anchor)
		this.navigateTo(anchor.href)
	}

	setActiveChip(active: HTMLAnchorElement) {
		const chips = q.ol<HTMLAnchorElement>(SELECTORS.chip, this.chipsEl)
		chips?.forEach((chip) => {
			const isActive = chip === active
			chip.classList.toggle('is-active', isActive)
			if (isActive) {
				chip.setAttribute('aria-current', 'page')
			} else {
				chip.removeAttribute('aria-current')
			}
		})
	}

	onPaginationClick(event: MouseEvent) {
		const target = event.target as HTMLElement | null
		if (!target) return
		const anchor = target.closest('a')
		if (!anchor) return
		event.preventDefault()
		this.navigateTo(anchor.href)
	}

	onSortChange(event: Event) {
		const select = q.rct<Event, HTMLSelectElement>(event)
		const value = select.value

		const current = new URL(window.location.href)
		const target = new URL(current.pathname, window.location.origin)
		const category = current.searchParams.get('category')
		if (category) {
			target.searchParams.set('category', category)
		}
		if (value && value !== 'newest') {
			target.searchParams.set('sort_by', value)
		}
		this.navigateTo(target.toString())
	}

	onPopState(_event: PopStateEvent) {
		this.renderFromUrl(window.location.href, { updateHistory: false })
	}

	navigateTo(url: string) {
		this.renderFromUrl(url, { updateHistory: true })
	}

	async renderFromUrl(url: string, opts: { updateHistory: boolean }) {
		this.setLoading(true)

		try {
			const fetchUrl = this.buildFetchUrl(url)
			const html = await this.fetchHtml(fetchUrl)
			this.swapContent(html)

			if (opts.updateHistory) {
				history.pushState({ blogGrid: true, url }, '', url)
			}

			this.scrollGridIntoView()
		} catch (error) {
			console.error('BlogGrid: failed to render', error)
			window.location.href = url
		} finally {
			this.setLoading(false)
		}
	}

	buildFetchUrl(url: string): string {
		const target = new URL(url, window.location.origin)
		target.searchParams.set('section_id', this.sectionId)
		return target.toString()
	}

	async fetchHtml(url: string): Promise<string> {
		const cached = BlogGrid.cache.get(url)
		if (cached) return cached.html

		const response = await fetch(url, {
			headers: { Accept: 'text/html' },
		})
		if (!response.ok) throw new Error(`BlogGrid: HTTP ${response.status}`)
		const html = await response.text()
		BlogGrid.cache.set(url, { html })
		return html
	}

	swapContent(html: string) {
		const parsed = new DOMParser().parseFromString(html, 'text/html')
		const incomingRoot = q.os(SELECTORS.root, parsed)
		if (!incomingRoot) {
			throw new Error('BlogGrid: incoming HTML missing <blog-grid> root')
		}

		const incomingChips = q.rs(SELECTORS.chips, incomingRoot)
		const incomingContent = q.rs(SELECTORS.content, incomingRoot)

		this.chipsEl.replaceWith(incomingChips)
		this.chipsEl = incomingChips
		this.contentEl.replaceWith(incomingContent)
		this.contentEl = incomingContent

		const incomingSortBy = q.oa(incomingRoot, 'data-sort-by') ?? 'newest'
		this.setAttribute('data-sort-by', incomingSortBy)

		this.bindChips()
		this.bindPagination()
		this.hydrateFromUrl()
	}

	setLoading(isLoading: boolean) {
		this.toggleAttribute('data-uc-loading', isLoading)
	}

	scrollGridIntoView() {
		const rect = this.getBoundingClientRect()
		if (rect.top < 0) {
			window.scrollTo({ top: window.scrollY + rect.top - 16, behavior: 'smooth' })
		}
	}
}
